import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
} from '@chakra-ui/react';
import { useMemo, useState, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { AsyncSelect, Select, MultiValue, SingleValue } from 'chakra-react-select';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { skipToken } from '@tanstack/react-query';
import { useDebounceCallback } from 'usehooks-ts';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';
import { DUKE_DOMAIN, emailHasAllowedDomain } from '@duke-hq/shared-constants/emails';
import { useProviderEmailDomains } from '../../hooks/useProviderEmailDomains';

import {
  type ConsultantRoleType,
  ConsultantRoleSchema,
  getRolesAvailable,
} from '@duke-hq/svc-api/provider/util/consultants';

export type TeamIndividualUserFormData = {
  id?: string;
  email: string;
  firstName: string;
  lastName: string;
  role: ConsultantRoleType | null;
  region?: string | null;
  site?: string | null;
  providerId: string | number | null;
  deletedAt?: Date | null;
};

type ConsultantSelectOption = {
  label: string;
  value: ConsultantRoleType;
};

type SelectOption = {
  label: string;
  value: string;
};

const defaultFormState: TeamIndividualUserFormData = {
  email: '',
  firstName: '',
  lastName: '',
  role: null,
  region: null,
  site: null,
  providerId: null,
  deletedAt: null,
};

export interface TeamIndividualUserFormRef {
  getValidatedData: () => Promise<TeamIndividualUserFormData | null>;
  resetForm: () => void;
}

interface TeamIndividualUserFormProps {
  member?: TeamIndividualUserFormData;
  allowRoleSelection?: boolean;
  onClose?: () => void;
  onFormStateChange: (isSubmitting: boolean, isLoading: boolean, isValid: boolean) => void;
}

export const TeamIndividualUserForm = forwardRef<
  TeamIndividualUserFormRef,
  TeamIndividualUserFormProps
>(({ member, allowRoleSelection = true, onFormStateChange }, ref): React.ReactElement => {
  const [roleSelected, setRoleSelected] = useState<ConsultantRoleType | null>(null);
  const [isStateOrRegionRoleSelected, setIsStateOrRegionRoleSelected] = useState<boolean>(false);
  const [isSiteOrConsultantRoleSelected, setIsSiteOrConsultantRoleSelected] =
    useState<boolean>(false);

  const [showRoleLocationGroupSelector, setShowRoleLocationGroupSelector] =
    useState<boolean>(!member);
  const [emailToValidate, setEmailToValidate] = useState<boolean>(false);

  const allowDukeDomain = !!(
    member &&
    typeof member.email === 'string' &&
    typeof DUKE_DOMAIN === 'string' &&
    member.email.endsWith(DUKE_DOMAIN)
  );

  const auth = useAuthTenant();
  const { allowedDomains, defaultDomain } = useProviderEmailDomains();
  const dropdownDomains = useMemo(
    () => allowedDomains.filter((domain) => domain !== DUKE_DOMAIN || domain === defaultDomain),
    [allowedDomains, defaultDomain],
  );
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);

  const memberDomain = useMemo(() => {
    if (member?.email != null && member.email.includes('@')) {
      return member.email.split('@')[1];
    }
    return null;
  }, [member?.email]);

  const emailDomain = useMemo(() => {
    if (member?.email?.endsWith(DUKE_DOMAIN) === true) {
      return DUKE_DOMAIN;
    }

    return selectedDomain ?? memberDomain ?? defaultDomain ?? auth?.user.tenant.email_domain;
  }, [member?.email, selectedDomain, memberDomain, defaultDomain, auth?.user.tenant.email_domain]);

  const {
    handleSubmit,
    register,
    control,
    clearErrors,
    reset,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting, isLoading, isValid },
  } = useForm<TeamIndividualUserFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: member
      ? {
          id: member.id,
          email: member?.email?.split('@')[0],
          firstName: member.firstName,
          lastName: member.lastName,
          role: ConsultantRoleSchema.safeParse(member?.role).data ?? null,
          region: member.region,
          site: member.site,
        }
      : {
          ...defaultFormState,
        },
  });

  const email = watch('email');
  const roleOptions = useMemo(() => getRolesAvailable(auth.user.role), [auth.user.role]);

  const { data: siteRegions, isPending: isRegionsPending } =
    trpcTenant.locations.listSiteRegions.useQuery(
      {},
      {
        enabled: Boolean(
          roleSelected &&
          (roleSelected === ConsultantRoleSchema.enum.STATE_MANAGER ||
            roleSelected === ConsultantRoleSchema.enum.REGIONAL_MANAGER),
        ),
      },
    );

  const siteRegionsOptions = useMemo(() => {
    return (
      siteRegions?.map((region) => ({
        label: region.name,
        value: region.id,
      })) || []
    );
  }, [siteRegions]);

  const loadSiteRegionsOptions = useDebounceCallback<
    (
      inputValue: string,
      callback: (
        opts: {
          value: string;
          label: string;
        }[],
      ) => void,
    ) => void
  >((inputValue: string, callback) => {
    if (siteRegionsOptions.length > 0) {
      callback(
        siteRegionsOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.value.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    } else {
      callback([]);
    }
  }, 500);

  const { data: siteLocations, isPending: isSitesPending } =
    trpcTenant.locations.listSiteLocations.useQuery(
      {
        filters: {
          available: true,
        },
      },
      {
        enabled: Boolean(
          roleSelected &&
          (roleSelected === ConsultantRoleSchema.enum.SITE_MANAGER ||
            roleSelected === ConsultantRoleSchema.enum.CONSULTANT),
        ),
      },
    );

  const sitesLocationsOptions = useMemo(() => {
    return (
      siteLocations?.map((site) => ({
        value: site.id,
        label: site.state ? `${site.name}, ${site.state}` : site.name,
      })) || []
    );
  }, [siteLocations]);

  const loadSitesLocationsOptions = useDebounceCallback<
    (
      inputValue: string,
      callback: (
        opts: {
          value: string;
          label: string;
        }[],
      ) => void,
    ) => void
  >((inputValue: string, callback) => {
    if (sitesLocationsOptions.length > 0) {
      callback(
        sitesLocationsOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.value.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    } else {
      callback([]);
    }
  }, 500);

  const { refetch: checkEmail } = trpcTenant.users.searchUser.useQuery(
    email
      ? {
          email: allowDukeDomain === true ? `${email}@${DUKE_DOMAIN}` : `${email}@${emailDomain}`,
        }
      : skipToken,
    {
      enabled: !!emailToValidate,
    },
  );

  const validateEmail = useCallback(async () => {
    if (email == null) {
      return true;
    }

    // Skip validation if site prop is passed and name is unchanged
    if (member?.email != null && member.email.length > 0 && email === member.email.split('@')[0]) {
      return true;
    }

    setEmailToValidate(true);

    const result = await checkEmail();
    setEmailToValidate(false);

    return result.data === false;
  }, [email, member, checkEmail]);

  const handleRoleChange = useCallback(
    (role: ConsultantRoleType) => {
      setRoleSelected(role);
      setIsStateOrRegionRoleSelected(false);
      setIsSiteOrConsultantRoleSelected(false);

      if (
        role === ConsultantRoleSchema.enum.STATE_MANAGER ||
        role === ConsultantRoleSchema.enum.REGIONAL_MANAGER
      ) {
        setIsStateOrRegionRoleSelected(true);
        setIsSiteOrConsultantRoleSelected(false);
        setValue('site', null);
      } else if (
        role === ConsultantRoleSchema.enum.SITE_MANAGER ||
        role === ConsultantRoleSchema.enum.CONSULTANT
      ) {
        setIsSiteOrConsultantRoleSelected(true);
        setIsStateOrRegionRoleSelected(false);
        setValue('region', null);
      }
    },
    [setRoleSelected, setIsStateOrRegionRoleSelected, setIsSiteOrConsultantRoleSelected, setValue],
  );

  const getValidatedData = useCallback(async (): Promise<TeamIndividualUserFormData | null> => {
    const currentFormData = await new Promise<TeamIndividualUserFormData | null>((resolve) => {
      handleSubmit(
        (data) => resolve(data),
        () => resolve(null),
      )();
    });

    if (currentFormData === null) {
      return null;
    }

    const isEmailUnique = await validateEmail();
    if (!isEmailUnique) {
      setError('email', {
        type: 'manual',
        message: `The email ${email}${
          allowDukeDomain ? `@${DUKE_DOMAIN}` : `@${emailDomain}`
        } is already taken. Please choose a unique email.`,
      });

      return null;
    }

    const fullEmail = currentFormData.email
      ? allowDukeDomain
        ? `${currentFormData.email}@${DUKE_DOMAIN}`
        : `${currentFormData.email}@${emailDomain}`
      : '';

    return { ...currentFormData, email: fullEmail };
  }, [handleSubmit, validateEmail, setError, email, allowDukeDomain, emailDomain]);

  useImperativeHandle(ref, () => ({
    getValidatedData: getValidatedData,
    resetForm: () =>
      reset(
        member
          ? {
              id: member.id,
              email: member?.email?.split('@')[0],
              firstName: member.firstName,
              lastName: member.lastName,
              role: ConsultantRoleSchema.safeParse(member?.role).data ?? null,
              site: member.site ?? null,
              region: member.region ?? null,
            }
          : defaultFormState,
      ),
  }));

  useEffect(() => {
    onFormStateChange(isSubmitting, isLoading, isValid);
  }, [isSubmitting, isLoading, isValid, onFormStateChange]);

  return (
    <Box gap={4} display="flex" flexDirection="column" w="full" py={4}>
      <FormControl isInvalid={!!errors.email} isRequired>
        <FormLabel htmlFor="email">
          <Trans>Email</Trans>
        </FormLabel>
        <InputGroup>
          <Input
            id="email"
            type="text"
            autoComplete="off"
            readOnly={member?.id != null}
            disabled={member?.id != null}
            borderRightRadius={!allowDukeDomain && dropdownDomains.length > 1 ? 0 : undefined}
            {...register('email', {
              required: t`Email is required`,
              validate: (value) => {
                if (value?.includes('@')) {
                  return t`Email cannot contain the '@' symbol. Please enter only the username.`;
                }

                const constructedEmail = `${value ?? ''}${allowDukeDomain ? `@${DUKE_DOMAIN}` : `@${emailDomain}`}`;
                const isValid = emailHasAllowedDomain(allowedDomains, constructedEmail);

                if (isValid === false) {
                  return t`Email must match one of: ${allowedDomains.map((domain) => `@${domain}`).join(', ')}`;
                }

                return true;
              },
              onChange: () => {
                if (errors.email) {
                  clearErrors('email');
                }
              },
            })}
          />
          {emailDomain ? (
            allowDukeDomain || dropdownDomains.length <= 1 ? (
              <InputRightAddon>
                {allowDukeDomain ? `@${DUKE_DOMAIN}` : `@${emailDomain}`}
              </InputRightAddon>
            ) : (
              <Box flex="0 0 40%">
                <Select
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }),
                  }}
                  isSearchable={false}
                  isDisabled={member?.id != null}
                  options={dropdownDomains.map((domain) => ({
                    label: `@${domain}`,
                    value: domain,
                  }))}
                  value={{ label: `@${emailDomain}`, value: emailDomain ?? '' }}
                  onChange={(option) => {
                    if (option != null) {
                      setSelectedDomain(option.value);
                    }
                  }}
                  chakraStyles={{
                    control: (provided) => ({
                      ...provided,
                      borderLeftRadius: 0,
                    }),
                  }}
                />
              </Box>
            )
          ) : null}
        </InputGroup>
        <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.firstName}>
        <FormLabel htmlFor="firstName">
          <Trans>First name</Trans>
        </FormLabel>
        <Input
          id="firstName"
          autoComplete="off"
          readOnly={!!member?.deletedAt}
          disabled={!!member?.deletedAt}
          {...register('firstName', {
            required: t`First name is required`,
            onChange: () => {
              if (errors.firstName) {
                clearErrors('firstName');
              }
            },
          })}
        />
        <FormErrorMessage>{errors.firstName?.message}</FormErrorMessage>
      </FormControl>

      <FormControl isRequired isInvalid={!!errors.lastName}>
        <FormLabel htmlFor="lastName">
          <Trans>Last name</Trans>
        </FormLabel>
        <Input
          id="lastName"
          autoComplete="off"
          readOnly={!!member?.deletedAt}
          disabled={!!member?.deletedAt}
          {...register('lastName', {
            required: t`Last name is required`,
            onChange: () => {
              if (errors.lastName) {
                clearErrors('lastName');
              }
            },
          })}
        />
        <FormErrorMessage>{errors.lastName?.message}</FormErrorMessage>
      </FormControl>

      {allowRoleSelection ? (
        <FormControl isRequired isInvalid={!!errors.role}>
          <FormLabel htmlFor="role">
            <Trans>Role</Trans>
          </FormLabel>
          {showRoleLocationGroupSelector ? (
            <Controller
              name="role"
              control={control}
              defaultValue={ConsultantRoleSchema.safeParse(member?.role).data ?? null}
              render={({ field }) => (
                <Select
                  inputId="role"
                  placeholder={t`Please select role`}
                  options={roleOptions}
                  value={roleOptions?.find(
                    (option: ConsultantSelectOption) => option.value === field.value,
                  )}
                  isDisabled={!!member?.deletedAt || auth.user.id === member?.id}
                  onChange={(
                    selectedOption:
                      | MultiValue<ConsultantSelectOption>
                      | SingleValue<ConsultantSelectOption>,
                  ) => {
                    if (
                      selectedOption != null &&
                      'value' in selectedOption &&
                      selectedOption?.label != null &&
                      selectedOption?.value != null
                    ) {
                      field.onChange(selectedOption?.value);
                      handleRoleChange(selectedOption?.value);
                    }
                  }}
                />
              )}
              rules={{ required: t`Please select a role` }}
            />
          ) : (
            <InputGroup>
              <Input
                readOnly
                value={
                  roleOptions?.find(
                    (option: ConsultantSelectOption) => option.value === member?.role,
                  )?.label
                }
              />
              <InputRightAddon>
                <Button
                  variant="link"
                  isDisabled={!!member?.deletedAt || auth.user.id === member?.id}
                  onClick={() => {
                    setShowRoleLocationGroupSelector(true);
                    setValue('role', null);
                  }}
                >
                  <Trans>Change</Trans>
                </Button>
              </InputRightAddon>
            </InputGroup>
          )}
          <FormErrorMessage>{errors.role?.message}</FormErrorMessage>
        </FormControl>
      ) : null}

      {roleSelected ? (
        <>
          {isStateOrRegionRoleSelected ? (
            <FormControl isInvalid={!!errors.region} isRequired>
              <FormLabel htmlFor="region">
                <Trans>Region</Trans>
              </FormLabel>
              <Controller
                name="region"
                control={control}
                render={({ field }) => (
                  <AsyncSelect
                    inputId="region"
                    name="region"
                    placeholder={t`Please select region`}
                    defaultOptions={siteRegionsOptions}
                    loadOptions={loadSiteRegionsOptions}
                    selectedOptionColorScheme={'brand'}
                    onChange={(val: SelectOption | null) => field.onChange(val?.value)}
                    isLoading={isRegionsPending}
                    isDisabled={siteRegionsOptions.length === 0}
                  />
                )}
                rules={{
                  required: t`Please select a region`,
                }}
              />
              <FormErrorMessage>{errors.region?.message}</FormErrorMessage>
            </FormControl>
          ) : null}

          {isSiteOrConsultantRoleSelected ? (
            <FormControl isInvalid={!!errors.site} isRequired>
              <FormLabel htmlFor="site">
                <Trans>Site</Trans>
              </FormLabel>
              <Controller
                name="site"
                control={control}
                render={({ field }) => (
                  <AsyncSelect
                    inputId="site"
                    name="site"
                    placeholder={t`Select site`}
                    defaultOptions={sitesLocationsOptions}
                    loadOptions={loadSitesLocationsOptions}
                    selectedOptionColorScheme={'brand'}
                    onChange={(val: SelectOption | null) => field.onChange(val?.value)}
                    isLoading={isSitesPending}
                    isDisabled={sitesLocationsOptions.length === 0}
                  />
                )}
                rules={{
                  required: t`Please select a site`,
                }}
              />
              <FormErrorMessage>{errors.site?.message}</FormErrorMessage>
            </FormControl>
          ) : null}
        </>
      ) : null}
    </Box>
  );
});

TeamIndividualUserForm.displayName = 'TeamIndividualUserForm';
